import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useTenantAreas } from '../../../../lib/access/useTenantAreas';
import { useApproveLaborCategoryMapping, useLaborCategoryAnchors, useProposeLaborCategoryMapping } from '../../../Delivery/Registries/hooks';
/**
 * Maps one entry to an occupational anchor. Same propose RPC as MapLaborCategoryDialog, from the
 * entry's side. Proposals land PENDING; a viewer with the registries authority (the same
 * `area:registries` rule the server enforces) has it approved straight away.
 */ export function MapEntryToAnchorDialog({ open, onOpenChange, tenantId, entryId }) {
    const canApprove = useTenantAreas().registries;
    const [search, setSearch] = useState('');
    const [anchorId, setAnchorId] = useState();
    const [relation, setRelation] = useState('EQUIVALENT');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const debounced = useDebounce(search, 250);
    const anchors = useLaborCategoryAnchors(tenantId, debounced, {
        enabled: open && !!tenantId
    });
    const propose = useProposeLaborCategoryMapping({
        announceSuccess: !canApprove
    });
    const approve = useApproveLaborCategoryMapping();
    const options = (anchors.data ?? []).map((a)=>({
            value: a.id,
            label: a.title,
            description: a.socCode,
            textValue: `${a.title} ${a.socCode}`
        }));
    const submit = async ()=>{
        if (!anchorId || busy) return;
        setBusy(true);
        setError('');
        try {
            const resp = await propose.mutateAsync({
                companyId: tenantId,
                fromEntryId: entryId,
                toAnchorId: anchorId,
                mappingType: relation,
                evidenceJson: JSON.stringify({
                    note
                })
            });
            if (canApprove && resp.mapping?.status === 'PENDING') await approve.mutateAsync({
                id: resp.mapping.id,
                reviewRevision: resp.mapping.reviewRevision
            });
            onOpenChange(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not map this labor category.');
        } finally{
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Map to an occupational anchor",
        footer: /*#__PURE__*/ _jsxs("div", {
            className: "flex w-full items-center justify-between gap-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: canApprove ? 'Approved as soon as you save.' : 'A person approves it in the review queue.'
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.registryLcatAnchor.entryMapSubmit,
                    disabled: !anchorId || busy,
                    onClick: ()=>void submit(),
                    children: canApprove ? 'Map' : 'Propose mapping'
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            "data-testid": Testids.registryLcatAnchor.entryMapDialog,
            children: [
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    "data-testid": Testids.registryLcatAnchor.entryMapError,
                    children: error
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Anchor",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Anchor",
                        options: options,
                        value: anchorId,
                        onValueChange: setAnchorId,
                        searchValue: search,
                        onSearchValueChange: setSearch,
                        shouldFilter: false,
                        loading: anchors.isFetching,
                        placeholder: "Search anchors by title or SOC",
                        emptyMessage: "No matching anchors."
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Relation to the anchor",
                    children: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Relation",
                        value: relation,
                        onValueChange: setRelation,
                        options: [
                            {
                                value: 'EQUIVALENT',
                                label: 'Equivalent'
                            },
                            {
                                value: 'NARROWER',
                                label: 'Narrower'
                            },
                            {
                                value: 'BROADER',
                                label: 'Broader'
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 3
                    })
                })
            ]
        })
    });
}
